import { useMemo, useState } from 'react';
import { getPreviewFootprints, getSpatialPreviewEntries, previewBounds, getPreviewRouteLabel } from './placeTreeSpatialPreviewModel.js';
import { getOverviewColor, getOverviewRenderLayer } from './placeOverviewMap.js';
import './PlaceTreeSpatialPreview.css';

const colorFor = entry => {
  if (entry.status === 'rejected') return '#c92a2a';
  if (entry.status === 'corrected') return '#b36b00';
  if (['planned', 'candidate'].includes(entry.status)) return '#8c64b0';
  return getOverviewColor({ ...entry.place, shape: entry.place.shape || entry.place.shapes?.[0] }).stroke;
};
const renderLayer = entry => getOverviewRenderLayer({ ...entry.place,
  shape: entry.place.shape || entry.place.shapes?.[0] }) + (['rejected', 'corrected', 'candidate'].includes(entry.status) ? 3 : 0);
const isValidationAttempt = entry => ['rejected', 'corrected', 'candidate'].includes(entry.status);
const entryLabel = entry => isValidationAttempt(entry)
  ? `${entry.name} (${entry.status} attempt ${entry.diagnostics?.attempt || 1}, not saved)`
  : `${entry.name} (${entry.status})`;
export default function PlaceTreeSpatialPreview({ draft, development }) {
  const [scope, setScope] = useState('town');
  const [zoom, setZoom] = useState(1);
  const [selectedId, setSelectedId] = useState('');
  const [showAttempts, setShowAttempts] = useState(false);
  const entries = useMemo(() => getSpatialPreviewEntries(draft, development), [draft, development]);
  const selected = entries.find(entry => entry.id === selectedId);
  const rootBounds = previewBounds(getPreviewFootprints(draft.draftPlace || {}));
  if (!rootBounds) return null;
  const rootX = (rootBounds.minX + rootBounds.maxX) / 2, rootZ = (rootBounds.minZ + rootBounds.maxZ) / 2;
  const focus = selected?.bounds;
  const allBounds = previewBounds(entries.filter(entry => entry.status !== 'context').flatMap(entry => entry.shapes)) || rootBounds;
  const frame = scope === 'nearby' ? allBounds : rootBounds;
  const size = Math.max(200, frame.maxX - frame.minX, frame.maxZ - frame.minZ) * 1.3 / zoom;
  const centerX = focus ? (focus.minX + focus.maxX) / 2 : scope === 'nearby' ? (frame.minX + frame.maxX) / 2 : rootX;
  const centerZ = focus ? (focus.minZ + focus.maxZ) / 2 : scope === 'nearby' ? (frame.minZ + frame.maxZ) / 2 : rootZ;
  const bounds = { minX: centerX - size / 2, maxX: centerX + size / 2, minZ: centerZ - size / 2, maxZ: centerZ + size / 2 };
  const visible = entries.filter(entry => (!isValidationAttempt(entry) || showAttempts || selectedId === entry.id) &&
    entry.bounds.maxX >= bounds.minX && entry.bounds.minX <= bounds.maxX && entry.bounds.maxZ >= bounds.minZ && entry.bounds.minZ <= bounds.maxZ)
    .sort((a, b) => renderLayer(a) - renderLayer(b));
  const transform = p => `${(p.x - bounds.minX) / size * 800},${(p.z - bounds.minZ) / size * 800}`;
  const scaleFeet = Math.pow(10, Math.floor(Math.log10(size / 5)));
  const traces = selected?.diagnostics?.attempts || (selected?.diagnostics ? [selected.diagnostics] : []);
  const unresolved = Object.entries(development.spatialDiagnostics || {}).filter(([id, d]) =>
    !development.finalRecords?.[id] && !d.attempts?.some(a => a.accepted)).map(([, d]) => d);
  const chooseScope = value => { setScope(value); setZoom(1); setSelectedId(''); };
  return <section className="editor-section place-tree-spatial-preview">
    <h3>Live Place Map</h3>
    <p>Updates after each prepared Place. The map shows accepted footprints and purple location plans. Rejected and repair attempts are hidden until you show or select them; those diagnostic footprints will not be saved.</p>
    <p><span style={{ color: '#000000' }}>━ Roads</span>{' · '}<span style={{ color: '#2563eb' }}>━ Rivers</span></p>
    <div className="form-actions">
      <button type="button" aria-pressed={scope === 'town'} onClick={() => chooseScope('town')}>Town view</button>
      <button type="button" aria-pressed={scope === 'nearby'} onClick={() => chooseScope('nearby')}>Nearby view</button>
      <button type="button" disabled={zoom >= 32} onClick={() => setZoom(value => value * 2)}>Zoom in</button>
      <button type="button" disabled={zoom <= .5} onClick={() => setZoom(value => value / 2)}>Zoom out</button>
      <button type="button" onClick={() => { setSelectedId(''); setZoom(1); }}>Reset view</button>
      <label><input type="checkbox" checked={showAttempts} onChange={event => {
        setShowAttempts(event.target.checked);
        if (!event.target.checked && selected && isValidationAttempt(selected)) setSelectedId('');
      }} /> Show rejected and repair attempts</label>
      <label>Inspect place <select aria-label="Inspect place" value={selectedId} onChange={event => setSelectedId(event.target.value)}>
        <option value="">Choose a footprint</option>
        <optgroup label="Current Places">
          {entries.filter(entry => !isValidationAttempt(entry)).map(entry => <option key={entry.id} value={entry.id}>{entryLabel(entry)}</option>)}
        </optgroup>
        <optgroup label="Validation attempts — not saved">
          {entries.filter(isValidationAttempt).map(entry => <option key={entry.id} value={entry.id}>{entryLabel(entry)}</option>)}
        </optgroup>
      </select></label>
    </div>
    {unresolved.map((diagnostics, i) => <p role="status" key={i}>{diagnostics.name} remains pending: {diagnostics.attempts?.at(-1)?.reason}</p>)}
    <div className="spatial-preview-layout">
      <svg viewBox="0 0 800 800" role="img" aria-label="Place Tree generation map, north at the top">
        <rect width="800" height="800" fill="#f3f7f3" />
        {visible.map(entry => <g key={entry.id} role="button" tabIndex="0" aria-label={`${entry.name}: ${entry.status}`}
          onClick={() => setSelectedId(entry.id)} onKeyDown={event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); setSelectedId(entry.id); } }}>
          <title>{entry.name}: {entry.status}{entry.place.approximate ? ' (partial context geometry)' : ''}</title>
          {entry.shapes.map((shape, i) => {
            const style = { stroke: colorFor(entry), strokeWidth: selectedId === entry.id ? 3 : 1.5, vectorEffect: 'non-scaling-stroke',
              strokeDasharray: ['corrected', 'planned', 'candidate'].includes(entry.status) ? '7 4' : undefined };
            if (shape.type === 'point') {
              const [cx, cy] = transform(shape.points[0]).split(',');
              return <circle key={i} cx={cx} cy={cy} r="5" fill={colorFor(entry)} />;
            }
            if (shape.type === 'path') return <polyline key={i} points={shape.points.map(transform).join(' ')} fill="none"
              stroke={colorFor(entry)} strokeDasharray={style.strokeDasharray} strokeWidth={Math.max(2, shape.width / size * 800)} />;
            return <polygon key={i} points={shape.points.map(transform).join(' ')} fill={colorFor(entry)} fillOpacity={entry.status === 'context' ? .06 : .14} {...style} />;
          })}
        </g>)}
        {visible.filter(entry => !isValidationAttempt(entry)).map(entry => {
          const position = getPreviewRouteLabel(entry, bounds);
          if (!position) return null;
          const [x, y] = transform(position).split(',');
          return <text key={`label:${entry.id}`} x={Number(x) + 8} y={Number(y) - 8} fontSize="13" fill={colorFor(entry)}
            stroke="#fff" strokeWidth="4" paintOrder="stroke" pointerEvents="none">{entry.name} ({entry.place.placeType || entry.place.mapRole || 'Path'})</text>;
        })}
        <text x="22" y="30" fill="#163522">↑ N</text>
        <line x1="22" y1="766" x2={22 + scaleFeet / size * 800} y2="766" stroke="#163522" strokeWidth="3" />
        <text x="22" y="750" fill="#163522">{scaleFeet.toLocaleString()} feet</text>
      </svg>
      <div className="spatial-preview-details" aria-live="polite">
        {selected ? <>
          <h4>{selected.name}</h4>
          {isValidationAttempt(selected) && <p><strong>{selected.status === 'rejected' ? 'Rejected' : 'Diagnostic'} attempt {selected.diagnostics?.attempt || 1} — this footprint will not be saved.</strong></p>}
          {(selected.place.placeType || selected.place.mapRole) && <p>Place type: {selected.place.placeType || selected.place.mapRole}</p>}
          {selected.place.idEntity && <p>Place ID: {selected.place.idEntity}</p>}
          <p>{selected.status}{selected.place.approximate ? ' · Partial context geometry' : ''}</p>
          <p>Width: {(selected.bounds.maxX - selected.bounds.minX).toFixed(1)} ft · Length: {(selected.bounds.maxZ - selected.bounds.minZ).toFixed(1)} ft</p>
          <p>Center X: {((selected.bounds.minX + selected.bounds.maxX) / 2).toFixed(1)} · Z: {((selected.bounds.minZ + selected.bounds.maxZ) / 2).toFixed(1)}</p>
          {traces.map((trace, i) => <div key={i}>
            <strong>Attempt {trace.attempt}: {trace.stage}</strong>
            {trace.reason && <p>{trace.reason}</p>}
            {!!trace.cornerAngles?.length && <p>Corner angles: {trace.cornerAngles.map(angle => angle === null ? 'invalid' : `${angle.toFixed(1)}°`).join(', ')}</p>}
          </div>)}
          <details><summary>Geometry and diagnostic data</summary><pre>{JSON.stringify({ location: selected.place.location, shape: selected.place.shape, diagnostics: selected.diagnostics }, null, 2)}</pre></details>
        </> : <p>Click a footprint or choose a place to inspect its coordinates and validation history.</p>}
      </div>
    </div>
  </section>;
}
